import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import LoggingAPI from "@/api/logging";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import type { Evidence } from "@/Features/Evidence/evidence.types";
import { evidencePhotosQueryKeys } from "@/Features/EvidencePhotos";
import { useCaseFiles } from "@/Features/Files/Hooks/useCaseFiles";
import { CaseFilesTable } from "@/Features/Files/Tables/CaseFilesTable";
import { CaseFileFlyout } from "@/Features/Files/Flyouts/CaseFileFlyout";
import { DeleteCaseFileModal } from "@/Features/Files/Modals/DeleteCaseFileModal";
import { EditCaseFileModal } from "@/Features/Files/Modals/EditCaseFileModal";
import { ShareItemModal } from "@/Features/Files/Modals/ShareItemModal";
import type { File as CaseFile } from "@/Features/Files/files.types";

/**
 * The Case Files table scoped to one evidence item's folder: the same rows,
 * modals and flyout as the case's Files tab, minus folders (an item folder is
 * flat). Uploads go through the evidence photo routes via the provider's
 * upload target, so a file uploaded here lands as `evidence_photo` and, if
 * it's an image, shows in the Photos tab too.
 */
export const EvidenceFilesTable: React.FC<{
  evidenceInfo: Evidence;
  folder: CaseFile | null;
}> = ({ evidenceInfo, folder }) => {
  const { evidence_id, case_id, case_uuid } = evidenceInfo;
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { handleFileClick, uploadFiles, tableRef } = useCaseFiles();

  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showEditFileModal, setShowEditFileModal] = useState(false);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CaseFile | null>(null);

  // Same gate as the Photos tab: the upload routes are the item's.
  const canUpload = hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE);

  // Same folder history as opening the folder from Case Files
  // (DocumentsPage): one entry when the item folder resolves, and again only
  // if it changes. No folder yet, no entry.
  const folderUuid = folder?.uuid;
  const userId = currentUser.user_id;
  useEffect(() => {
    if (!folderUuid) return;
    LoggingAPI.createLog({
      case_id,
      user_id: userId,
      message: `Accessed folder`,
      event: "case_folder:access",
      object_type: "folder",
      object_uuid: folderUuid,
    });
  }, [folderUuid, case_id, userId]);

  const closeDeleteFileModal = () => {
    setShowDeleteFileModal(false);
    setSelectedItem(null);
  };

  // The Files API mutations only invalidate the Files lists, and the Photos
  // tab stays mounted (and its list fresh for 30 minutes) while this tab is
  // open, so a rename or delete here has to refresh it too.
  const refreshAfterChange = () => {
    tableRef?.current?.refresh();
    queryClient.invalidateQueries({
      queryKey: evidencePhotosQueryKeys.list(evidence_id),
    });
  };

  const handleUploadFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.style.display = "none";
    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      uploadFiles(Array.from(files));

      input.remove();
    };
    input.click();
  };

  // Same as the Case Files page, minus its folder branch: an item folder
  // holds no folder rows.
  const handleDownload = (rowData: CaseFile) => {
    const downloadUrl = rowData?.link?.replace(
      "localhost:3000",
      "localhost:3001"
    ); // replace is used for local development

    if (downloadUrl) {
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = rowData.name;
      anchor.click();
      anchor.remove();

      LoggingAPI.createLog({
        case_id,
        user_id: currentUser.user_id,
        message: `Downloaded File`,
        event: "case_file:download",
        object_type: "file",
        object_uuid: rowData.uuid,
      });
    }
  };

  const select = (setShow: (show: boolean) => void) => (rowData: CaseFile) => {
    setSelectedItem(rowData);
    setShow(true);
  };

  return (
    <>
      <CaseFilesTable
        tableRef={tableRef}
        stateStoreKey="evidence:files:table"
        // No folder yet: an empty table, no fetch (the case root is not it).
        enabled={!!folder}
        query={{ case_uuid, parent_uuid: folder?.uuid }}
        onUploadFile={canUpload ? handleUploadFile : undefined}
        dropDisabled={!canUpload}
        onActionButtonClick={select(setShowInfoPanel)}
        onShowDetails={select(setShowInfoPanel)}
        onDownload={handleDownload}
        onView={handleFileClick}
        onUpdate={select(setShowEditFileModal)}
        onDelete={select(setShowDeleteFileModal)}
        onShareViaRelay={select(setShowShareModal)}
      />
      {showInfoPanel && selectedItem && (
        <CaseFileFlyout
          open={showInfoPanel}
          onClose={() => setShowInfoPanel(false)}
          onDelete={() => {
            setShowInfoPanel(false);
            setShowDeleteFileModal(false);
            refreshAfterChange();
          }}
          defaultData={{
            ...selectedItem,
            case: {
              uuid: case_uuid,
              case_id,
              case_name: "",
              case_number: "",
            },
          }}
        />
      )}
      {showEditFileModal && selectedItem && (
        <EditCaseFileModal
          show={showEditFileModal}
          onCancel={() => {
            setShowEditFileModal(false);
            setSelectedItem(null);
          }}
          onClose={() => {
            setShowEditFileModal(false);
            setSelectedItem(null);
          }}
          onSubmit={() => {
            setShowEditFileModal(false);
            setSelectedItem(null);
            refreshAfterChange();
          }}
          defaultFormData={selectedItem}
        />
      )}
      {showDeleteFileModal && selectedItem && (
        <DeleteCaseFileModal
          show={showDeleteFileModal}
          onClose={closeDeleteFileModal}
          onCancel={closeDeleteFileModal}
          onSubmit={() => {
            closeDeleteFileModal();
            refreshAfterChange();
          }}
          defaultFormData={selectedItem}
        />
      )}
      {showShareModal && selectedItem && (
        <ShareItemModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          onSubmit={() => setShowShareModal(false)}
          onCancel={() => setShowShareModal(false)}
          defaultValues={{
            selectedItems: [selectedItem],
            caseInfo: { uuid: case_uuid, case_id },
          }}
        />
      )}
    </>
  );
};

export default EvidenceFilesTable;
